import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Button, CircularProgress, Box, Typography, Chip,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { getTailoredResume } from '../services/api';

/**
 * Modal shown when the user clicks "Apply" on a job or application.
 * Offers to generate (or view) a tailored resume before opening the
 * external job posting.
 *
 * Props:
 *  - open            : bool
 *  - onClose         : () => void
 *  - application     : { id, role, company, url, description }
 */
export default function TailorPromptModal({ open, onClose, application }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [existingTailor, setExistingTailor] = useState(null);

  // Check if a tailored resume already exists for this application
  useEffect(() => {
    if (!open || !application?.id) return;
    let cancelled = false;
    setChecking(true);
    setExistingTailor(null);

    getTailoredResume(application.id)
      .then((res) => { if (!cancelled) setExistingTailor(res.data); })
      .catch((err) => {
        // 404 is expected when no tailored resume exists yet — not an error
        if (!cancelled && err.response?.status !== 404) {
          console.error('Failed to check tailored resume:', err);
        }
      })
      .finally(() => { if (!cancelled) setChecking(false); });

    return () => { cancelled = true; };
  }, [open, application?.id]);

  const hasDescription = !!(application?.description && application.description.trim());
  const hasUrl = !!(application?.url);
  const alreadyTailored = !!existingTailor;

  const handleTailor = () => {
    onClose();
    navigate(`/tailor/${application.id}`);
  };

  const handleUseOriginal = () => {
    onClose();
    if (hasUrl) window.open(application.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <AutoFixHighIcon color="primary" />
          <Typography variant="h6" component="span" fontWeight="bold">
            {t('tailor_modal.title')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        {checking ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : alreadyTailored ? (
          <>
            <DialogContentText sx={{ mb: 1.5, fontSize: 14 }}>
              <Trans
                i18nKey="tailor_modal.already_tailored"
                values={{
                  role: application?.role || '',
                  company: application?.company || '',
                }}
                components={{ strong: <strong /> }}
              />
            </DialogContentText>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              <Chip
                size="small"
                label={t('tailor_modal.version_label', { count: existingTailor.generation_count })}
                color="primary"
                variant="outlined"
              />
              <Chip
                size="small"
                label={t('tailor_modal.regens_remaining', { count: existingTailor.regens_remaining_for_app })}
                variant="outlined"
              />
            </Box>
          </>
        ) : !hasDescription ? (
          <DialogContentText sx={{ fontSize: 14 }}>
            {t('tailor_modal.no_description')}
          </DialogContentText>
        ) : (
          <>
            <DialogContentText sx={{ mb: 0.5, fontSize: 14 }}>
              <Trans
                i18nKey="tailor_modal.intro"
                values={{
                  role: application?.role || '',
                  company: application?.company || '',
                }}
                components={{ strong: <strong /> }}
              />
            </DialogContentText>
            <DialogContentText variant="caption" color="text.secondary">
              {t('tailor_modal.duration_credits')}
            </DialogContentText>
          </>
        )}
      </DialogContent>

      {/* Actions: stack vertically on mobile (xs), inline on tablet+ (sm+).
          Primary CTA first on mobile so thumb reaches it easily. */}
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          pt: 1,
          gap: 1,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: 'stretch',
          '& > button': {
            marginLeft: { xs: '0 !important', sm: undefined },
          },
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          size="small"
          sx={{ alignSelf: { xs: 'center', sm: 'auto' } }}
        >
          {t('common.cancel')}
        </Button>

        {hasUrl && (
          <Button
            onClick={handleUseOriginal}
            variant="outlined"
            size="small"
          >
            {t('tailor_modal.use_original')}
          </Button>
        )}

        {!checking && hasDescription && (
          <Button
            onClick={handleTailor}
            variant="contained"
            size="small"
            startIcon={alreadyTailored ? <VisibilityIcon /> : <AutoFixHighIcon />}
          >
            {alreadyTailored ? t('tailor_modal.view') : t('tailor_modal.tailor')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
